import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, type ApiClient, type User } from "@ferrivia/api-contract";
import type { TokenStore } from "./tokenStore";

export type SessionStatus = "loading" | "anonymous" | "authenticated" | "offline" | "error";

/** "cached" means `user` came from an on-device snapshot, not the current
    `/me` call — meaningful only while `status === "authenticated"`. Anything
    with a side effect (a POST, a passkey check, an analytics identity) must
    wait for "verified" so a cache-hydrated offline shell never fires a
    mutation that's bound to fail or double up once real connectivity
    returns. */
export type SessionTrust = "cached" | "verified";

export interface SessionCredentials {
  token: string;
  expiresAt: string;
}

export interface Session {
  status: SessionStatus;
  user: User | null;
  trust: SessionTrust;
  signIn(credentials: SessionCredentials): Promise<void>;
  signOut(): Promise<void>;
  refresh(): Promise<void>;
  /** The 401 choke point: any API call, not just `/me`, can discover the
      token is dead (signed out elsewhere, expired server-side). Same local
      reset as a 401 from `refresh()` — clears the token and drops to
      anonymous — without the network round trip `signOut()` makes to
      `/auth/logout`, which would itself just 401. Idempotent. */
  handleUnauthorized(): void;
}

export const SessionContext = createContext<Session | null>(null);

export interface CachedSessionSeed {
  user: User;
}

export function useSessionState(
  api: ApiClient,
  tokenStore: TokenStore,
  readCachedSession?: () => CachedSessionSeed | null,
): Session {
  // Bounded by the token's own declared expiry, not just the cache's own
  // max-age (enforced by readCachedSession itself) — a cached shell must
  // never outlive the session it was hydrated from.
  const cachedSeed = useMemo(() => {
    const seed = readCachedSession?.();
    if (!seed) return null;
    const expiry = tokenStore.getExpiry();
    if (expiry === undefined || expiry <= Date.now()) return null;
    return seed;
    // Read once, at mount, only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const hadCachedSeed = useRef(cachedSeed !== null);

  const [status, setStatus] = useState<SessionStatus>(cachedSeed ? "authenticated" : "loading");
  const [user, setUser] = useState<User | null>(cachedSeed?.user ?? null);
  const [trust, setTrust] = useState<SessionTrust>(cachedSeed ? "cached" : "verified");

  const refresh = useCallback(async () => {
    if (!tokenStore.get()) {
      setUser(null);
      setStatus("anonymous");
      setTrust("verified");
      return;
    }
    try {
      const me = await api.me();
      setUser(me);
      setStatus("authenticated");
      setTrust("verified");
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          tokenStore.clear();
          setUser(null);
          setStatus("anonymous");
          setTrust("verified");
          return;
        }
        // The server answered and said no — not a connectivity problem. A
        // cache-hydrated shell is still better than dropping to an error
        // screen, so leave it up rather than overwriting it.
        if (!hadCachedSeed.current) setStatus("error");
        return;
      }
      // Not an ApiError means fetch never got a response — transport, not
      // HTTP. Never rethrow: an unhandled rejection here is what used to pin
      // status at "loading" forever and blank the app offline. Same cached-
      // shell carve-out as above.
      if (!hadCachedSeed.current) setStatus("offline");
    }
  }, [api, tokenStore]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // A transport failure is retried once the browser reports connectivity —
  // otherwise a session stays stuck at "offline" until something else
  // triggers a manual refresh (e.g. a reload).
  useEffect(() => {
    if (status !== "offline") return;
    const onOnline = () => void refresh();
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [status, refresh]);

  const signIn = useCallback(
    async (credentials: SessionCredentials) => {
      tokenStore.set(credentials.token, credentials.expiresAt);
      await refresh();
    },
    [refresh, tokenStore],
  );

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      tokenStore.clear();
      setUser(null);
      setStatus("anonymous");
      setTrust("verified");
    }
  }, [api, tokenStore]);

  const handleUnauthorized = useCallback(() => {
    tokenStore.clear();
    setUser(null);
    setStatus("anonymous");
    setTrust("verified");
  }, [tokenStore]);

  return { status, user, trust, signIn, signOut, refresh, handleUnauthorized };
}

export function useSession(): Session {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error("useSession must be used within an AuthProvider");
  }
  return ctx;
}
